<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  visibleInputSchemaProperties,
  isInputSchemaPropertyRequired,
  getToolDisplayName,
  humanizeSnakeCase,
} from 'dashboard/helpers/toolHelper';

const props = defineProps({
  tools: {
    type: Object,
    required: true,
    validator: value => {
      return (
        typeof value === 'object' &&
        'loading' in value &&
        'error' in value &&
        'tools' in value &&
        Array.isArray(value.tools)
      );
    },
  },
});

const { t } = useI18n();

const isCollapsed = ref(true);

const visiblePropertiesForTool = tool =>
  visibleInputSchemaProperties(tool.inputSchema);

const hasVisibleArguments = tool => {
  const properties = visiblePropertiesForTool(tool);
  return Object.keys(properties).length > 0;
};
</script>

<template>
  <div class="mt-4 pt-4 border-t border-slate-200">
    <div
      v-if="props.tools.loading"
      class="flex items-center gap-2 text-sm text-slate-600"
    >
      <div class="w-4 h-4 relative">
        <div
          class="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin will-change-transform"
        />
      </div>
      <span>{{ t('INTEGRATION_SETTINGS.MCP_SERVERS.TOOLS.LOADING') }}</span>
    </div>

    <div v-else-if="props.tools.error" class="text-sm text-red-600">
      <Icon name="i-lucide-alert-circle" class="w-4 h-4 inline mr-1" />
      {{ props.tools.error }}
    </div>

    <div
      v-else-if="props.tools.tools.length === 0"
      class="text-sm text-slate-500"
    >
      {{ t('INTEGRATION_SETTINGS.MCP_SERVERS.TOOLS.NONE') }}
    </div>

    <div v-else>
      <Button
        icon="i-lucide-wrench"
        variant="ghost"
        size="sm"
        justify="start"
        class="mb-3 px-2 gap-2"
        @click="isCollapsed = !isCollapsed"
      >
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium">{{
            props.tools.tools.length
          }}</span>
        </div>
      </Button>
      <div v-if="!isCollapsed" class="space-y-3">
        <div
          v-for="tool in props.tools.tools"
          :key="tool.name"
          class="bg-slate-50 rounded-lg p-3 border"
        >
          <div class="flex items-start gap-3">
            <Icon
              icon="i-lucide-tool"
              class="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0"
            />
            <div class="flex-1 min-w-0">
              <div class="font-medium text-slate-900 mb-1">
                {{ getToolDisplayName(tool) }}
              </div>
              <div v-if="tool.description" class="text-slate-600 text-sm mb-3">
                {{ tool.description }}
              </div>

              <!-- Arguments section -->
              <div v-if="hasVisibleArguments(tool)" class="space-y-2">
                <div
                  class="text-xs font-medium text-slate-700 uppercase tracking-wide"
                >
                  {{ t('INTEGRATION_SETTINGS.MCP_SERVERS.TOOLS.ARGUMENTS') }}
                </div>
                <div class="space-y-2">
                  <div
                    v-for="(propSchema, propName) in visiblePropertiesForTool(
                      tool
                    )"
                    :key="propName"
                    class="flex items-start justify-between gap-2 py-1"
                  >
                    <div class="flex-1">
                      <div class="flex items-center gap-2">
                        <span class="text-xs font-medium text-slate-800">
                          {{ humanizeSnakeCase(propName) }}
                        </span>
                        <span class="text-xs text-slate-500">
                          {{ propSchema.type || 'unknown' }}
                        </span>
                        <span
                          v-if="
                            isInputSchemaPropertyRequired(
                              tool.inputSchema,
                              propName
                            )
                          "
                          class="text-xs text-red-600 font-medium"
                        >
                          {{
                            t('INTEGRATION_SETTINGS.MCP_SERVERS.TOOLS.REQUIRED')
                          }}
                        </span>
                      </div>
                      <div
                        v-if="propSchema.description"
                        class="text-xs text-slate-600 mt-1"
                      >
                        {{ propSchema.description }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
